import { useEffect, useState, useCallback } from 'react';
import { HelpButton } from '../../components/HelpButton';
import { ConfirmDialog, Pagination } from '../../components';
import { staffUsersService, staffTeamsService, staffPartnersService, staffVolunteerRoleService, STAFF_PAGE_SIZE } from '../../lib/api-service';
import type { PartnerDetail, StaffUser, Team } from '../../lib/types';
import { getTextColorHex } from '../../utils/color-contrast';
import { formatDate } from '../../utils/date';
import { PartnerMultiSelect } from '../../components/PartnerAutocomplete';
import { Plus, Pencil, Trash2, X, Loader2, Shield, ShieldCheck, Handshake, Mail, HandHelping, Search, Upload, Download } from 'lucide-react';
import { DateInput } from '../../components/DateInput';

type UserForm = {
  email: string;
  first_name: string;
  last_name: string;
  phone: string;
  date_of_birth: string;
  is_staff: boolean;
  is_active: boolean;
  is_admin: boolean;
  is_volunteer: boolean;
  team_ids: number[];
  partner_ids: number[];
};

const EMPTY_FORM: UserForm = {
  email: '', first_name: '', last_name: '', phone: '', date_of_birth: '',
  is_staff: true, is_active: true, is_admin: false, is_volunteer: false, team_ids: [], partner_ids: [],
};

export default function ManageUsers() {
  const [users, setUsers] = useState<StaffUser[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [teams, setTeams] = useState<Team[]>([]);
  const [partners, setPartners] = useState<PartnerDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [form, setForm] = useState<UserForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<'' | 'staff' | 'admin' | 'volunteer' | 'partner' | 'volunteer_request'>('');
  const [deleteTarget, setDeleteTarget] = useState<StaffUser | null>(null);
  const [showImport, setShowImport] = useState(false);
  const [csvImportText, setCsvImportText] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ created_count: number; skipped_count: number; error_count: number; errors: string[] } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, t, p] = await Promise.all([
        staffUsersService.list({ page, search: searchQuery || undefined }),
        staffTeamsService.list(),
        staffPartnersService.list(),
      ]);
      setUsers(u.results);
      setTotalCount(u.count);
      setTeams(t.results);
      setPartners(p.results);
    } catch { setError('Eroare la încărcare.'); }
    finally { setLoading(false); setInitialLoad(false); }
  }, [page, searchQuery]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [searchQuery]);
  useEffect(() => {
    const t = setTimeout(() => setSearchQuery(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  function startAdd() { setIsAdding(true); setEditingId(null); setForm(EMPTY_FORM); setError(''); }
  function startEdit(u: StaffUser) {
    setEditingId(u.id); setIsAdding(false); setError('');
    setForm({
      email: u.email, first_name: u.first_name, last_name: u.last_name,
      phone: u.profile?.phone || '', date_of_birth: u.profile?.date_of_birth || '',
      is_staff: u.is_staff, is_active: u.is_active, is_admin: u.is_admin, is_volunteer: !!u.profile?.is_volunteer,
      team_ids: u.teams.map((t) => t.id),
      partner_ids: (u.partner_names || []).map((p) => p.id),
    });
  }
  function cancel() { setIsAdding(false); setEditingId(null); setForm(EMPTY_FORM); setError(''); }

  async function handleSave() {
    if (!form.email.trim()) { setError('Email-ul este obligatoriu.'); return; }

    setSaving(true); setError('');
    try {
      const payload: Record<string, unknown> = {
        email: form.email, first_name: form.first_name, last_name: form.last_name,
        is_staff: form.is_staff, is_active: form.is_active, is_admin: form.is_admin,
        team_ids: form.team_ids, partner_ids: form.partner_ids,
        profile: { phone: form.phone, date_of_birth: form.date_of_birth || null, is_volunteer: form.is_volunteer },
      };
      if (isAdding) await staffUsersService.create(payload);
      else if (editingId) await staffUsersService.update(editingId, payload);
      cancel(); await load();
    } catch { setError('Eroare la salvare. Verifică datele.'); }
    finally { setSaving(false); }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    try { await staffUsersService.delete(deleteTarget.id); setDeleteTarget(null); await load(); }
    catch { setError('Eroare la ștergere.'); }
  }

  async function handleResendInvite(id: number) {
    setError(''); setSuccessMsg('');
    try {
      await staffUsersService.resendInvite(id);
      setSuccessMsg('Invitația a fost retrimisă.');
      setTimeout(() => setSuccessMsg(''), 4000);
    } catch {
      setError('Eroare la retrimiterea invitației.');
    }
  }

  async function toggleActive(u: StaffUser) {
    try { await staffUsersService.update(u.id, { is_active: !u.is_active }); await load(); }
    catch { setError('Eroare.'); }
  }

  async function reviewVolunteerRequest(u: StaffUser, action: 'approve' | 'reject') {
    try {
      await staffVolunteerRoleService.reviewRequest(u.id, { action });
      await load();
    } catch (err: any) {
      setError(err?.message || 'Eroare.');
    }
  }

  async function handleBulkImport() {
    if (!csvImportText.trim()) return;
    setImporting(true);
    setImportResult(null);
    setError('');
    try {
      const res = await staffUsersService.bulkImport(csvImportText);
      setImportResult(res);
      setCsvImportText('');
      if (res.created_count > 0) await load();
    } catch (err: any) {
      setError(err?.message || 'Eroare la import.');
    } finally {
      setImporting(false);
    }
  }

  function downloadTemplate() {
    const csv = 'first_name,last_name,email,phone,date_of_birth,category,team\nIon,Popescu,ion.popescu@example.com,+40712345678,2000/01/15,voluntar,Logistică\n';
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'import_utilizatori_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  async function toggleVolunteer(u: StaffUser) {
    try {
      await staffVolunteerRoleService.toggle(u.id);
      await load();
    } catch (err: any) {
      setError(err?.message || 'Eroare la actualizarea rolului de voluntar.');
    }
  }

  function toggleTeam(id: number) {
    setForm((f) => ({
      ...f, team_ids: f.team_ids.includes(id) ? f.team_ids.filter((t) => t !== id) : [...f.team_ids, id],
    }));
  }

  function togglePartner(id: number) {
    setForm((f) => ({
      ...f, partner_ids: f.partner_ids.includes(id) ? f.partner_ids.filter((p) => p !== id) : [...f.partner_ids, id],
    }));
  }

  if (initialLoad) return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[var(--vivid-purple)]" /></div>;

  const formOpen = isAdding || editingId !== null;
  const inputClass = "w-full rounded-lg border px-3 py-2.5 text-sm outline-none focus:border-[var(--vivid-purple)] focus:ring-2 focus:ring-[var(--vivid-purple)]/20";

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-primary)' }}>Utilizatori</h1>
          <HelpButton to="/staff/documentatie/utilizatori" />
        </div>
        {!formOpen && (
          <div className="flex gap-2">
            <button onClick={() => setShowImport(!showImport)} className="flex items-center gap-2 rounded-lg border border-[var(--vivid-purple)] px-4 py-2.5 text-sm font-semibold text-[var(--vivid-purple)] hover:bg-[var(--vivid-purple)]/5">
              <Upload className="h-4 w-4" /> Import CSV
            </button>
            <button onClick={startAdd} className="flex items-center gap-2 rounded-lg bg-[var(--vivid-purple)] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[var(--vivid-purple)]/90">
              <Plus className="h-4 w-4" /> Utilizator Nou
            </button>
          </div>
        )}
      </div>

      {error && <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      {/* CSV Import section */}
      {showImport && !formOpen && (
        <div className="mb-6 rounded-xl border bg-white p-5 shadow-sm">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-lg font-bold" style={{ fontFamily: 'var(--font-primary)' }}>Import utilizatori din CSV</h2>
            <button onClick={() => { setShowImport(false); setImportResult(null); }} className="text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>
          </div>
          <p className="mb-3 text-xs text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
            Coloane: <code>first_name</code>, <code>last_name</code>, <code>email</code>, <code>phone</code>, <code>date_of_birth</code> (YYYY/MM/DD), <code>category</code> (staff/voluntar), <code>team</code>.
            Utilizatorii sunt creați fără parolă. Emailurile duplicate sunt ignorate.
          </p>
          <div className="mb-3">
            <button onClick={downloadTemplate} className="flex items-center gap-1.5 text-xs font-medium text-[var(--vivid-purple)] hover:underline">
              <Download className="h-3.5 w-3.5" /> Descarcă template CSV
            </button>
          </div>
          <div className="mb-3 flex items-center gap-3">
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  const reader = new FileReader();
                  reader.onload = (ev) => setCsvImportText(ev.target?.result as string || '');
                  reader.readAsText(file);
                }
              }}
              className="text-sm text-muted-foreground file:mr-3 file:rounded-lg file:border-0 file:bg-[var(--vivid-purple)]/10 file:px-3 file:py-2 file:text-sm file:font-medium file:text-[var(--vivid-purple)]"
            />
          </div>
          <textarea
            value={csvImportText}
            onChange={(e) => setCsvImportText(e.target.value)}
            rows={4}
            className="mb-3 w-full rounded-lg border px-3 py-2 text-xs font-mono"
            placeholder={'first_name,last_name,email,phone,date_of_birth,category,team\nIon,Popescu,ion@example.com,+40712345678,2000/01/15,voluntar,Logistică'}
          />
          {importResult && (
            <div className="mb-3 rounded-lg bg-green-50 px-4 py-3 text-sm">
              <p className="font-medium text-green-700">{importResult.created_count} utilizatori creați{importResult.skipped_count > 0 && `, ${importResult.skipped_count} ignorați (email existent)`}.</p>
              {importResult.errors.length > 0 && (
                <ul className="mt-1 text-xs text-red-600">
                  {importResult.errors.map((err, i) => <li key={i}>{err}</li>)}
                </ul>
              )}
            </div>
          )}
          <button
            onClick={handleBulkImport}
            disabled={importing || !csvImportText.trim()}
            className="flex items-center gap-2 rounded-lg bg-[var(--vivid-purple)] px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-40"
          >
            <Upload className="h-4 w-4" />
            {importing ? 'Se importă...' : 'Importă utilizatori'}
          </button>
        </div>
      )}
      {successMsg && <div className="mb-4 rounded-lg bg-green-50 px-4 py-3 text-sm text-green-700">{successMsg}</div>}

      {/* Search & role filter */}
      {!formOpen && (
        <div className="mb-4 flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search"
              placeholder="Caută după nume, email, telefon..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full rounded-lg border py-2.5 pl-10 pr-10 text-sm outline-none focus:border-[var(--vivid-purple)] focus:ring-2 focus:ring-[var(--vivid-purple)]/20"
              style={{ fontFamily: 'var(--font-secondary)' }}
            />
            {loading && (
              <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
            )}
          </div>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value as typeof roleFilter)}
            className="rounded-lg border bg-white px-3 py-2.5 text-sm"
            style={{ fontFamily: 'var(--font-secondary)' }}
          >
            <option value="">Toate rolurile</option>
            <option value="staff">Staff</option>
            <option value="admin">Admin</option>
            <option value="volunteer">Voluntar</option>
            <option value="volunteer_request">Cereri voluntariat</option>
            <option value="partner">Prieten</option>
          </select>
        </div>
      )}

      {formOpen && (
        <div className="mb-6 rounded-xl border bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-bold">{isAdding ? 'Utilizator Nou' : 'Editează Utilizator'}</h2>
            <button onClick={cancel} className="text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="mb-1.5 block text-sm font-medium">Email (= username) *</label>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} autoFocus className={inputClass} />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium">Prenume</label>
              <input type="text" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className={inputClass} />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium">Nume</label>
              <input type="text" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className={inputClass} />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium">Telefon</label>
              <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+40..." className={inputClass} />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium">Data nașterii</label>
              <DateInput value={form.date_of_birth} onChange={(v) => setForm({ ...form, date_of_birth: v })} className={inputClass} />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1.5 block text-sm font-medium">Echipe</label>
              <div className="flex flex-wrap gap-2">
                {teams.map((t) => (
                  <button key={t.id} type="button" onClick={() => toggleTeam(t.id)}
                    className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${form.team_ids.includes(t.id) ? 'border-transparent' : 'border-gray-200 bg-white'}`}
                    style={form.team_ids.includes(t.id) ? { backgroundColor: t.color, color: getTextColorHex(t.color) } : {}}
                  >
                    {t.name}
                  </button>
                ))}
                {teams.length === 0 && <span className="text-xs text-muted-foreground">Nicio echipă definită.</span>}
              </div>
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1.5 block text-sm font-medium">Autorizat pentru organizații prietene</label>
              <PartnerMultiSelect
                partners={partners.map((p) => ({ id: p.id, name: p.name, slug: p.slug, logo: p.logo }))}
                selectedIds={form.partner_ids}
                excludeIds={form.partner_ids}
                onChange={(id) => togglePartner(id)}
              />
              {form.partner_ids.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {form.partner_ids.map((id) => {
                    const p = partners.find((p) => p.id === id);
                    if (!p) return null;
                    return (
                      <span key={id} className="inline-flex items-center gap-1.5 rounded-full border border-[var(--vivid-purple)]/20 bg-[var(--vivid-purple)]/5 px-3 py-1.5 text-xs font-medium text-[var(--vivid-purple)]">
                        <Handshake className="h-3 w-3" />
                        {p.name}
                        <button type="button" onClick={() => togglePartner(id)} className="ml-0.5 hover:text-red-500">
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
              <p className="mt-1 text-xs text-muted-foreground">Selectează organizațiile pe care acest utilizator le poate administra.</p>
            </div>
            <div className="space-y-4 sm:col-span-2">
              <div className="flex items-start justify-between rounded-lg border p-4">
                <div className="flex-1">
                  <span className="text-sm font-medium">Activ</span>
                  <p className="mt-0.5 text-xs text-muted-foreground">Utilizatorii inactivi nu se pot autentifica. Dezactivează în loc să ștergi pentru a păstra istoricul.</p>
                </div>
                <label className="relative ml-4 inline-flex cursor-pointer items-center">
                  <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="peer sr-only" />
                  <div className="h-6 w-11 rounded-full bg-gray-300 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all peer-checked:bg-green-500 peer-checked:after:translate-x-full" />
                </label>
              </div>
              <div className="flex items-start justify-between rounded-lg border p-4">
                <div className="flex-1">
                  <span className="text-sm font-medium">Staff</span>
                  <p className="mt-0.5 text-xs text-muted-foreground">Permite accesul la zona de administrare. Utilizatorii staff pot gestiona activități, prieteni și tag-uri.</p>
                </div>
                <label className="relative ml-4 inline-flex cursor-pointer items-center">
                  <input type="checkbox" checked={form.is_staff} onChange={(e) => setForm({ ...form, is_staff: e.target.checked })} className="peer sr-only" />
                  <div className="h-6 w-11 rounded-full bg-gray-300 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all peer-checked:bg-[var(--vivid-purple)] peer-checked:after:translate-x-full" />
                </label>
              </div>
              <div className="flex items-start justify-between rounded-lg border p-4">
                <div className="flex-1">
                  <span className="text-sm font-medium">Admin</span>
                  <p className="mt-0.5 text-xs text-muted-foreground">Acces complet: poate gestiona utilizatori, echipe și toate setările platformei. Acordă cu atenție.</p>
                </div>
                <label className="relative ml-4 inline-flex cursor-pointer items-center">
                  <input type="checkbox" checked={form.is_admin} onChange={(e) => setForm({ ...form, is_admin: e.target.checked })} className="peer sr-only" />
                  <div className="h-6 w-11 rounded-full bg-gray-300 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all peer-checked:bg-[var(--vivid-orange)] peer-checked:after:translate-x-full" />
                </label>
              </div>
              <div className="flex items-start justify-between rounded-lg border p-4">
                <div className="flex-1">
                  <span className="text-sm font-medium">Voluntar</span>
                  <p className="mt-0.5 text-xs text-muted-foreground">Activează rolul de voluntar pentru acest utilizator. Voluntarii pot fi asignați la activități și pot aplica pentru poziții.</p>
                </div>
                <label className="relative ml-4 inline-flex cursor-pointer items-center">
                  <input type="checkbox" checked={form.is_volunteer} onChange={(e) => {
                    if (e.target.checked) {
                      if (!form.date_of_birth) { setError('Data nașterii este obligatorie pentru rolul de voluntar.'); return; }
                      const dob = new Date(form.date_of_birth);
                      const today = new Date();
                      const age = today.getFullYear() - dob.getFullYear() - (today < new Date(today.getFullYear(), dob.getMonth(), dob.getDate()) ? 1 : 0);
                      if (age < 15) { setError('Utilizatorul trebuie să aibă cel puțin 15 ani pentru rolul de voluntar.'); return; }
                    }
                    setForm({ ...form, is_volunteer: e.target.checked });
                  }} className="peer sr-only" />
                  <div className="h-6 w-11 rounded-full bg-gray-300 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all peer-checked:bg-green-500 peer-checked:after:translate-x-full" />
                </label>
              </div>
            </div>
          </div>
          <div className="mt-5 flex gap-2">
            <button onClick={cancel} className="rounded-lg border px-4 py-2.5 text-sm font-medium text-muted-foreground hover:bg-gray-50">Anulează</button>
            <button onClick={handleSave} disabled={saving} className="rounded-lg bg-[var(--vivid-purple)] px-6 py-2.5 text-sm font-semibold text-white hover:bg-[var(--vivid-purple)]/90 disabled:opacity-60">
              {saving ? 'Se salvează...' : isAdding ? 'Creează' : 'Salvează'}
            </button>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="overflow-hidden rounded-xl border bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-gray-50/50">
              <th className="px-4 py-3 text-left font-semibold">Utilizator</th>
              <th className="hidden px-4 py-3 text-left font-semibold sm:table-cell">Telefon</th>
              <th className="hidden px-4 py-3 text-left font-semibold md:table-cell">Vârstă</th>
              <th className="hidden px-4 py-3 text-left font-semibold md:table-cell">Echipe</th>
              <th className="hidden px-4 py-3 text-left font-semibold lg:table-cell">Organizații</th>
              <th className="px-4 py-3 text-left font-semibold">Status</th>
              <th className="px-4 py-3 text-right font-semibold">Acțiuni</th>
            </tr>
          </thead>
          <tbody>
            {users.filter((u) => {
              if (!roleFilter) return true;
              if (roleFilter === 'staff') return u.is_staff;
              if (roleFilter === 'admin') return u.is_admin;
              if (roleFilter === 'volunteer') return !!u.profile?.is_volunteer;
              if (roleFilter === 'volunteer_request') return u.profile?.volunteer_request_status === 'pending';
              if (roleFilter === 'partner') return u.partner_names && u.partner_names.length > 0;
              return true;
            }).map((u) => (
              <tr key={u.id} className="border-b last:border-b-0 hover:bg-gray-50/50">
                <td className="px-4 py-3">
                  <div className="font-medium">{u.first_name} {u.last_name}</div>
                  <div className="text-xs text-muted-foreground">{u.email}</div>
                </td>
                <td className="hidden px-4 py-3 sm:table-cell text-xs text-muted-foreground">{u.profile?.phone || '—'}</td>
                <td className="hidden px-4 py-3 md:table-cell text-xs text-muted-foreground">{u.age !== null ? `${u.age} ani` : '—'}</td>
                <td className="hidden px-4 py-3 md:table-cell">
                  <div className="flex flex-wrap gap-1">
                    {u.teams.map((t) => (
                      <span key={t.id} className="rounded-full px-2 py-0.5 text-[10px] font-medium" style={{ backgroundColor: t.color, color: getTextColorHex(t.color) }}>
                        {t.name}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="hidden px-4 py-3 lg:table-cell">
                  <div className="flex flex-wrap gap-1">
                    {(u.partner_names || []).map((p) => (
                      <span key={p.id} className="rounded-full bg-[var(--vivid-purple)]/10 px-2 py-0.5 text-[10px] font-medium text-[var(--vivid-purple)]">
                        {p.name}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <button onClick={() => toggleActive(u)} className={`rounded-full px-2 py-0.5 text-xs font-medium ${u.is_active ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {u.is_active ? 'Activ' : 'Inactiv'}
                    </button>
                    {u.is_admin ? (
                      <ShieldCheck className="h-3.5 w-3.5 text-[var(--vivid-orange)]" />
                    ) : u.is_staff ? (
                      <Shield className="h-3.5 w-3.5 text-[var(--vivid-purple)]" />
                    ) : null}
                    {u.partner_names && u.partner_names.length > 0 && (
                      <Handshake className="h-3.5 w-3.5 text-[var(--vivid-cyan)]" />
                    )}
                    {u.profile?.is_volunteer && (
                      <span title="Voluntar"><HandHelping className="h-3.5 w-3.5 text-green-600" /></span>
                    )}
                    {u.profile?.volunteer_request_status === 'pending' && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
                        Cerere voluntar
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-1">
                    {u.profile?.volunteer_request_status === 'pending' ? (
                      <>
                        <button
                          onClick={() => reviewVolunteerRequest(u, 'approve')}
                          className="rounded-lg px-2 py-1 text-xs font-medium text-green-600 hover:bg-green-50"
                        >
                          Aprobă
                        </button>
                        <button
                          onClick={() => reviewVolunteerRequest(u, 'reject')}
                          className="rounded-lg px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
                        >
                          Respinge
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => toggleVolunteer(u)}
                        className={`rounded-lg p-2 transition-colors ${u.profile?.is_volunteer ? 'text-green-600 hover:bg-green-50' : 'text-muted-foreground hover:bg-gray-100'}`}
                        title={u.profile?.is_volunteer ? 'Dezactivează voluntar' : 'Activează voluntar'}
                      >
                        <HandHelping className="h-4 w-4" />
                      </button>
                    )}
                    {u.profile?.volunteer_request_status !== 'pending' && (
                      <button onClick={() => handleResendInvite(u.id)} className="rounded-lg p-2 text-muted-foreground hover:bg-blue-50 hover:text-blue-600" title="Retrimite invitația"><Mail className="h-4 w-4" /></button>
                    )}
                    <button onClick={() => startEdit(u)} className="rounded-lg p-2 text-muted-foreground hover:bg-gray-100 hover:text-[var(--vivid-purple)]" title="Editează"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => setDeleteTarget(u)} className="rounded-lg p-2 text-muted-foreground hover:bg-red-50 hover:text-red-600" title="Șterge"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {users.length === 0 && <div className="py-12 text-center text-sm text-muted-foreground">Niciun utilizator.</div>}
      </div>

      <Pagination page={page} totalCount={totalCount} pageSize={STAFF_PAGE_SIZE} onPageChange={setPage} />

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title="Șterge utilizatorul"
        description={`Sigur vrei să ștergi utilizatorul „${deleteTarget?.email}"? Acțiunea este ireversibilă.`}
      />
    </div>
  );
}
